<template>
  <div :key="$i18n.locale + 'custom-date-picker'">
    <slot name="default" v-bind="{ inputClass }">
      <v-text-field
        v-model="displayValue"
        :class="inputClass"
        :readonly="!editable"
        v-bind="$attrs"
        :error-messages="dateError"
      />
    </slot>

    <date-picker
      :key="$i18n.locale + 'cdp'"
      v-model="pickerValue"
      :auto-submit="autoSubmit"
      :custom-input="`.${inputClass}`"
      :disable="disable"
      :format="displayFormat"
      :display-format="displayFormat"
      :max="max"
      dense
      :min="min"
      :multiple="multiple"
      :range="$attrs.range"
      :locale="locale"
      :calendar="calendarType"
      :type="format"
      :is-rtl="$i18n.locale === 'fa' || $i18n.locale === 'ar'"
      outlined
    />
  </div>
</template>

<script>
import moment from "moment-jalaali";

export default {
  name: "CustomDatePicker",
  props: {
    value: [String, Array],
    autoSubmit: { type: Boolean, default: true },
    format: { type: String, default: "datetime" }, //: datetime, date, time
    inputClass: { type: String, default: "custom-input" },
    min: String,
    max: String,
    nullable: Boolean,
    sp: { type: String, default: "/" },
    editable: Boolean,
    multiple: Boolean,
    sortable: Boolean,
    disable: Array,
    expression: String,
  },
  data: () => ({
    dateError: null,
  }),
  computed: {
    locale() {
      return this.$i18n.locale === "fa"
        ? "fa"
        : this.$i18n.locale === "ar"
        ? "ar"
        : "en";
    },
    calendarType() {
      return this.$i18n.locale === "fa" ? "persian" : "gregorian";
    },
    isPersian() {
      return this.$i18n.locale === "fa";
    },
    displayFormat() {
      if (this.format === "time") {
        return "HH:mm";
      }

      const sp = this.sp;
      if (this.isPersian) {
        return this.format === "datetime"
          ? `jYYYY${sp}jMM${sp}jDD HH:mm:ss`
          : `jYYYY${sp}jMM${sp}jDD`;
      } else if (this.$i18n.locale === "ar") {
        return this.format === "datetime"
          ? `DD${sp}MM${sp}YYYY HH:mm:ss`
          : `DD${sp}MM${sp}YYYY`;
      } else {
        return this.format === "datetime"
          ? `MM${sp}DD${sp}YYYY HH:mm:ss`
          : `MM${sp}DD${sp}YYYY`;
      }
    },
    getFormat() {
      const sp = this.sp;
      return this.$i18n.locale === "ar"
        ? `DD${sp}MM${sp}YYYY`
        : `MM${sp}DD${sp}YYYY`;
    },
    displayValue: {
      get() {
        if (!this.value) {
          return this.nullable
            ? null
            : this.nowDateTimeString(this.sp, this.format);
        }
        const normalized = Array.isArray(this.value)
          ? this.value.map((date) => this.normalizeNumerals(date))
          : this.normalizeNumerals(this.value);
        return this.toDisplayFormat(normalized);
      },
      set(val) {
        if (!val) {
          this.dateError = null;
          this.$emit(
            "input",
            this.nullable ? null : this.nowDateTimeString(this.sp, this.format),
          );
          return;
        }
        const normalizedVal = Array.isArray(val)
          ? val.map((date) => this.normalizeNumerals(date))
          : this.normalizeNumerals(val);
        try {
          const gregorianVal = this.toGregorian(normalizedVal);
          this.dateError = null;
          this.$emit("input", this.checkSort(gregorianVal));
        } catch (error) {
          this.dateError = this.$t("form.invalid_date");
        }
      },
    },
    pickerValue: {
      get() {
        if (!this.value) {
          return this.nullable
            ? null
            : this.nowDateTimeString(this.sp, this.format);
        }
        const normalized = Array.isArray(this.value)
          ? this.value.map((date) => this.normalizeNumerals(date))
          : this.normalizeNumerals(this.value);
        return this.toDisplayFormat(normalized);
      },
      set(val) {
        if (!val) {
          this.dateError = null;
          this.$emit(
            "input",
            this.nullable ? null : this.nowDateTimeString(this.sp, this.format),
          );
          return;
        }
        const normalizedVal = Array.isArray(val)
          ? val.map((date) => this.normalizeNumerals(date))
          : this.normalizeNumerals(val);
        try {
          const gregorianVal = this.toGregorian(normalizedVal);
          this.dateError = null;
          this.$emit("input", this.checkSort(gregorianVal));
        } catch (error) {
          this.dateError = this.$t("form.invalid_date");
        }
      },
    },
  },
  mounted() {
    if (!this.value && !this.nullable) {
      this.$emit("input", this.nowDateTimeString(this.sp, this.format));
    }
    this.updateTranslations();

    const input = document.querySelector(`.${this.inputClass}`);
    if (input) {
      input.addEventListener("click", () => {
        setTimeout(() => {
          this.updateTranslations();
        }, 300);
      });
    }

    const delays = [100, 500, 1000, 2000];
    delays.forEach((delay) => {
      setTimeout(() => {
        this.updateTranslations();
      }, delay);
    });
  },
  watch: {
    "$i18n.locale": {
      handler() {
        this.updateTranslations();
        const delays = [10, 50, 100, 200];
        delays.forEach((delay) => {
          setTimeout(() => {
            this.updateTranslations();
          }, delay);
        });
        this.$forceUpdate();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    updateTranslations() {
      this.$nextTick(() => {
        const actionButtons = document.querySelectorAll(".vpd-actions button");
        if (actionButtons.length >= 3) {
          actionButtons[0].textContent = this.$t("form.select");
          actionButtons[1].textContent = this.$t("form.clear");
          actionButtons[2].textContent = this.$t("form.now");
        }
      });
    },
    normalizeNumerals(date) {
      if (!date) return date;
      const arabicNumerals = "٠١٢٣٤٥٦٧٨٩";
      const persianNumerals = "۰۱۲۳۴۵۶۷۸۹";
      const westernNumerals = "0123456789";
      return date
        .replace(
          /[٠١٢٣٤٥٦٧٨٩]/g,
          (match) => westernNumerals[arabicNumerals.indexOf(match)],
        )
        .replace(
          /[۰۱۲۳۴۵۶۷۸۹]/g,
          (match) => westernNumerals[persianNumerals.indexOf(match)],
        );
    },
    toGregorian(date) {
      if (!date) return this.nowDateTimeString(this.sp, this.format);
      const normalized = Array.isArray(date)
        ? date.map((d) => this.normalizeNumerals(d))
        : this.normalizeNumerals(date);
      if (Array.isArray(normalized)) {
        return normalized.map((d) => this.convertToGregorianSingle(d));
      }
      return this.convertToGregorianSingle(normalized);
    },
    convertToGregorianSingle(date) {
      if (!date) return this.nowDateTimeString(this.sp, this.format);

      if (this.format === "time") {
        if (date.includes(" ")) return date.split(" ")[1].substring(0, 5);
        return date.length >= 5 ? date.substring(0, 5) : date;
      }

      if (moment(date, "YYYY-MM-DD HH:mm:ss", true).isValid()) {
        return date;
      }
      let parsedDate;
      if (this.isPersian) {
        if (moment(date, "jYYYY/jMM/jDD HH:mm:ss", true).isValid()) {
          parsedDate = moment(date, "jYYYY/jMM/jDD HH:mm:ss").toDate();
        } else if (moment(date, "jYYYY/jMM/jDD", true).isValid()) {
          parsedDate = moment(date, "jYYYY/jMM/jDD").toDate();
        }
      } else {
        const format =
          this.$i18n.locale === "ar"
            ? "DD/MM/YYYY HH:mm:ss"
            : "MM/DD/YYYY HH:mm:ss";
        const dateOnlyFormat =
          this.$i18n.locale === "ar" ? "DD/MM/YYYY" : "MM/DD/YYYY";
        if (moment(date, format, true).isValid()) {
          parsedDate = moment(date, format).toDate();
        } else if (moment(date, dateOnlyFormat, true).isValid()) {
          parsedDate = moment(date, dateOnlyFormat).toDate();
        }
      }
      if (!parsedDate || isNaN(parsedDate)) {
        return this.nowDateTimeString(this.sp, this.format);
      }
      return moment(parsedDate).format(
        this.format === "datetime" ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD",
      );
    },
    toDisplayFormat(date) {
      if (!date) return this.nowDateTimeString(this.sp, this.format);
      const normalized = Array.isArray(date)
        ? date.map((d) => this.normalizeNumerals(d))
        : this.normalizeNumerals(date);
      if (Array.isArray(normalized)) {
        return normalized.map((d) => this.toDisplayFormatSingle(d));
      }
      return this.toDisplayFormatSingle(normalized);
    },
    toDisplayFormatSingle(date) {
      if (!date) return this.nowDateTimeString(this.sp, this.format);

      if (this.format === "time") {
        if (date.includes(" ")) return date.split(" ")[1].substring(0, 5);
        return date.length >= 5 ? date.substring(0, 5) : date;
      }

      let parsedDate;
      if (moment(date, "YYYY-MM-DD HH:mm:ss", true).isValid()) {
        parsedDate = moment(date, "YYYY-MM-DD HH:mm:ss");
      } else if (moment(date, "YYYY-MM-DD", true).isValid()) {
        parsedDate = moment(date, "YYYY-MM-DD");
      } else if (
        this.isPersian &&
        moment(date, "jYYYY/jMM/jDD HH:mm:ss", true).isValid()
      ) {
        parsedDate = moment(date, "jYYYY/jMM/jDD HH:mm:ss");
      } else if (
        this.isPersian &&
        moment(date, "jYYYY/jMM/jDD", true).isValid()
      ) {
        parsedDate = moment(date, "jYYYY/jMM/jDD");
      } else {
        const format =
          this.$i18n.locale === "ar"
            ? "DD/MM/YYYY HH:mm:ss"
            : "MM/DD/YYYY HH:mm:ss";
        const dateOnlyFormat =
          this.$i18n.locale === "ar" ? "DD/MM/YYYY" : "MM/DD/YYYY";
        if (moment(date, format, true).isValid()) {
          parsedDate = moment(date, format);
        } else if (moment(date, dateOnlyFormat, true).isValid()) {
          parsedDate = moment(date, dateOnlyFormat);
        } else {
          return this.nowDateTimeString(this.sp, this.format);
        }
      }
      if (this.isPersian) {
        return moment(parsedDate).format(
          this.format === "datetime"
            ? `jYYYY${this.sp}jMM${this.sp}jDD HH:mm:ss`
            : `jYYYY${this.sp}jMM${this.sp}jDD`,
        );
      } else if (this.$i18n.locale === "ar") {
        return moment(parsedDate).format(
          this.format === "datetime"
            ? `DD${this.sp}MM${this.sp}YYYY HH:mm:ss`
            : `DD${this.sp}MM${this.sp}YYYY`,
        );
      } else {
        return moment(parsedDate).format(
          this.format === "datetime"
            ? `MM${this.sp}DD${this.sp}YYYY HH:mm:ss`
            : `MM${this.sp}DD${this.sp}YYYY`,
        );
      }
    },
    checkSort(val) {
      if (!this.sortable || !Array.isArray(val)) return val;
      return val
        .map((d) => this.toGregorian(d))
        .sort((a, b) => new Date(a) - new Date(b));
    },
    nowDateTimeString(sp, format) {
      if (format === "time") {
        return moment().format("HH:mm");
      }

      if (this.isPersian) {
        return moment().format(
          format === "datetime"
            ? `jYYYY${sp}jMM${sp}jDD HH:mm:ss`
            : `jYYYY${sp}jMM${sp}jDD`,
        );
      } else if (this.$i18n.locale === "ar") {
        return moment().format(
          format === "datetime"
            ? `DD${sp}MM${sp}YYYY HH:mm:ss`
            : `DD${sp}MM${sp}YYYY`,
        );
      } else {
        return moment().format(
          format === "datetime"
            ? `MM${sp}DD${sp}YYYY HH:mm:ss`
            : `MM${sp}DD${sp}YYYY`,
        );
      }
    },
  },
};
</script>

<style scoped>
.custom-input {
  font-family: "Roboto", sans-serif;
}
</style>
